
import { supabase, enhancedQuery } from "@/integrations/supabase/client";
import { AircraftType } from "@/types/flight";

type AircraftTypeCreateInput = {
  name: string;
  code: string;
};

type AircraftTypeUpdateInput = {
  name?: string;
  code?: string;
};

// Get all aircraft types
export const getAircraftTypes = async (): Promise<AircraftType[]> => {
  try {
    const { data, error } = await supabase
      .from('aircraft_types')
      .select('*')
      .order('name');
    
    if (error) {
      console.error('Error fetching aircraft types:', error);
      throw error;
    }
    
    return data as AircraftType[];
  } catch (error) {
    console.error('Unexpected error in getAircraftTypes:', error);
    throw error;
  }
};

// Get a single aircraft type by ID
export const getAircraftTypeById = async (id: string): Promise<AircraftType> => {
  return enhancedQuery(async () => {
    const result = await supabase
      .from('aircraft_types')
      .select('*')
      .eq('id', id)
      .single();
      
    return result;
  });
};

// Create a new aircraft type
export const createAircraftType = async (input: AircraftTypeCreateInput): Promise<AircraftType> => {
  try {
    const { data, error } = await supabase
      .from('aircraft_types')
      .insert([
        {
          name: input.name,
          code: input.code,
        }
      ])
      .select();
    
    if (error) {
      console.error('Error creating aircraft type:', error);
      throw error;
    }
    
    if (!data || data.length === 0) {
      throw new Error('Failed to create aircraft type: No data returned');
    }
    
    return data[0] as AircraftType;
  } catch (error) {
    console.error('Unexpected error in createAircraftType:', error);
    throw error;
  }
};

// Update an existing aircraft type
export const updateAircraftType = async (id: string, input: AircraftTypeUpdateInput): Promise<AircraftType> => {
  try {
    const { data, error } = await supabase
      .from('aircraft_types')
      .update({
        ...(input.name !== undefined && { name: input.name }),
        ...(input.code !== undefined && { code: input.code }),
      })
      .eq('id', id)
      .select();
    
    if (error) {
      console.error('Error updating aircraft type:', error);
      throw error;
    }
    
    if (!data || data.length === 0) {
      throw new Error('Failed to update aircraft type: Aircraft type not found');
    }
    
    return data[0] as AircraftType;
  } catch (error) {
    console.error('Unexpected error in updateAircraftType:', error);
    throw error;
  }
};

// Delete an aircraft type
export const deleteAircraftType = async (id: string): Promise<void> => {
  try {
    const { error } = await supabase
      .from('aircraft_types')
      .delete()
      .eq('id', id);
    
    if (error) {
      console.error('Error deleting aircraft type:', error);
      throw error;
    }
  } catch (error) {
    console.error('Unexpected error in deleteAircraftType:', error);
    throw error;
  }
};
